import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useDirectoryServices } from '@xemplo/directory-services';
import { useHttpClient } from '@xemplo/http';
import { XemploStateScope } from '@xemplo/types';
import { useAppSettings } from '@xemplo/xemplo-app-settings';

import {
  XemploStateEndpoint,
  XemploStateQueryKeys,
} from './xemplo-state-query.constants';
import { UseUpdateXemploStateProps } from './xemplo-state-query.types';

/**
 * React Query mutation hook to replace (PUT) entire state data.
 *
 * This hook completely replaces the state at the specified scope with new data.
 * Use this when you want to overwrite all existing state values.
 * Automatically invalidates the state cache on success.
 *
 * @returns React Query mutation object
 *
 *@remark It always returns 204. So the response is always undefined. Check for error instead of result to determine if the operation was successful.
 *
 * @example
 * ```ts
 * // Using with safe error handling
 * const { mutateAsync } = usePutXemploState();
 * const [result, error] = await safeCallAsync(
 *   mutateAsync({
 *     scope: XemploStateScopes.Session,
 *     data: { param1: 'value1' }
 *   })
 * );
 * if (error) {
 *   console.error('Failed to update state:', error);
 * } else {
 *   console.log('State updated:', result);
 * }
 * ```
 */
export function usePutXemploStateMutation<
  TData extends Record<string, unknown> = Record<string, unknown>,
>() {
  const { dsUrl } = useAppSettings();
  const { client } = useHttpClient({ withCredentials: true });
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ scope, data }: UseUpdateXemploStateProps<TData>) => {
      const url = new URL(XemploStateEndpoint.create(scope), dsUrl).toString();
      return await client.put<void>(url, data, { withCredentials: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [XemploStateQueryKeys.getState] });
    },
  });
}

/**
 * React Query mutation hook to partially update (PATCH) state data.
 *
 * This hook merges the provided data with existing state at the specified scope.
 * Use this when you want to update specific fields without affecting other values.
 * Automatically invalidates the state cache on success.
 *
 * @template TResult - The expected shape of the returned updated state data
 *
 * @returns React Query mutation object with typed result
 *
 * @example
 * ```ts
 * // Basic usage - update only specific fields
 * const { mutateAsync } = usePatchXemploState();
 *
 * const [result, error] = await safeCallAsync(
 *   mutateAsync({
 *     scope: XemploStateScopes.Session,
 *     data: { param1: 'value1' }
 *   })
 * );
 * ```
 */
export function usePatchXemploStateMutation<
  TData extends Record<string, unknown> = Record<string, unknown>,
  TResult extends Record<string, unknown> = Record<string, unknown>,
>() {
  const { dsUrl } = useAppSettings();
  const { client } = useHttpClient({ withCredentials: true });
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ data, scope }: UseUpdateXemploStateProps<TData>) => {
      const url = new URL(XemploStateEndpoint.create(scope), dsUrl).toString();
      return await client
        .patch<TResult>(url, data, { withCredentials: true })
        .then((resp) => resp.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [XemploStateQueryKeys.getState] });
    },
  });
}

/**
 * This is a standalone version of the patch mutation that allows passing
 * an auth token directly. It's useful for contexts where the host app
 * does not consume the `@xemplo/auth-provider` and thus cannot use the
 * standard `usePatchXemploStateMutation` hook.
 *
 * @param authToken - The auth token is nullable because in some cases we need
 * to wait for the user to be authenticated before we can perform the mutation,
 * but we still want to have access to the hook before that.
 */
export function usePatchXemploStateMutationStandalone<
  TData extends Record<string, unknown> = Record<string, unknown>,
  TResult extends Record<string, unknown> = Record<string, unknown>,
>(authToken?: string) {
  // Stabilize ds config to avoid unnecessary re-renders of the mutation hook
  const config = useMemo(() => ({ authToken }), [authToken]);
  const directoryServices = useDirectoryServices(config);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ data, scope }: UseUpdateXemploStateProps<TData>) => {
      return await directoryServices
        .patchXemploState<TResult>(scope, data)
        .then((resp) => resp.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [XemploStateQueryKeys.getState] });
    },
  });
}

export function useDeleteXemploStateMutation() {
  const { dsUrl } = useAppSettings();
  const { client } = useHttpClient({ withCredentials: true });
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (scope: XemploStateScope) => {
      const url = new URL(XemploStateEndpoint.create(scope), dsUrl).toString();
      return await client
        .delete<void>(url, { withCredentials: true })
        .then((resp) => resp.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [XemploStateQueryKeys.getState] });
    },
  });
}
